import { z } from "zod"

import {
  DEFAULT_COVAL_VOICE,
  DEFAULT_LANGUAGE_CODE,
} from "@/types/ModelConfigs/CovalVoiceConstants"
import { isSituateSpeakerPresetId } from "@/types/ModelConfigs/VoiceModelConfigs"
import { ValidationPresets } from "@/utils/validation-presets"

// Default prompt for silent mode personas
const SILENT_MODE_DEFAULT_PROMPT =
  "This persona is configured for Silent Mode and will not speak during simulations."

const faceCustomizationSchema = z.object({
  hair: z.string(),
  eyes: z.string(),
  lips: z.string(),
})

/** Shown when a persona has both Audio Degradation and Situate Speaker set. */
export const AUDIO_DEGRADATION_SITUATE_SPEAKER_CONFLICT_MESSAGE =
  "Audio Degradation can't be combined with Situate Speaker. Turn off Situate Speaker to use Audio Degradation."

export const personaFormSchema = z
  .object({
    display_name: ValidationPresets.strings.short.refine(
      (name) => name.trim().length > 0,
      "Display name is required",
    ),
    metadata: ValidationPresets.records.default.optional(),
    simulated_user_prompt: ValidationPresets.strings.prompt.optional(),
    avatar_url: ValidationPresets.strings.url,
    face_customization: faceCustomizationSchema,
    persona_role: z.string().optional(),
    persona_personality: z.string().optional(),
    persona_speech: z.string().optional(),
  })
  .refine(
    (data) =>
      isSilentMode(data.metadata) ||
      !data.simulated_user_prompt ||
      data.simulated_user_prompt.trim().length > 0,
    {
      message: "Enter persona instructions or leave the prompt empty.",
      path: ["simulated_user_prompt"],
    },
  )
  .refine(
    (data) =>
      // A speaking persona needs a voice and language. Silent personas get
      // defaults in the transform below, so the requirement is skipped there.
      isSilentMode(data.metadata) || hasVoiceConfiguration(data.metadata),
    {
      message: "Select a voice and language",
      path: ["metadata"],
    },
  )
  .refine(
    (data) =>
      // Silent mode hides the entire Voice & Audio section, so these fields
      // can't be reconciled by the user while it's on — don't hold a stale
      // conflict against a persona that isn't surfacing it.
      isSilentMode(data.metadata) ||
      !(hasAudioDegradation(data.metadata) && hasSituateSpeaker(data.metadata)),
    {
      message: AUDIO_DEGRADATION_SITUATE_SPEAKER_CONFLICT_MESSAGE,
      path: ["metadata"],
    },
  )
  .transform((data) => {
    // If silent mode is enabled, fill in defaults only for empty fields.
    // Preserve existing voice/language/prompt so they aren't wiped from the DB
    // when the user later disables silent mode.
    if (isSilentMode(data.metadata)) {
      return {
        ...data,
        metadata: {
          ...data.metadata,
          voice_name:
            (data.metadata?.voice_name as string | undefined) ||
            DEFAULT_COVAL_VOICE,
          language_code:
            (data.metadata?.language_code as string | undefined) ||
            DEFAULT_LANGUAGE_CODE,
        },
        simulated_user_prompt: data.simulated_user_prompt?.trim()
          ? data.simulated_user_prompt
          : SILENT_MODE_DEFAULT_PROMPT,
      }
    }

    return data
  })

export type PersonaFormData = z.infer<typeof personaFormSchema>

export type PersonaFormErrors = Partial<Record<keyof PersonaFormData, string>>

/**
 * Type-safe helper to check if persona is in silent mode.
 * Use this instead of `as any` when checking silent mode.
 */
export function isSilentMode(metadata: unknown): boolean {
  if (metadata == null || typeof metadata !== "object") {
    return false
  }
  return "silent_mode" in metadata && metadata.silent_mode === true
}

/**
 * True when the persona can speak: either a library voice with a language, or
 * a custom designed voice, which carries its own language.
 */
function hasVoiceConfiguration(metadata: unknown): boolean {
  if (metadata == null || typeof metadata !== "object") {
    return false
  }
  const { voice_name, language_code, custom_voice_id } = metadata as {
    voice_name?: unknown
    language_code?: unknown
    custom_voice_id?: unknown
  }
  if (typeof custom_voice_id === "string" && custom_voice_id.length > 0) {
    return true
  }
  return (
    typeof voice_name === "string" &&
    voice_name.length > 0 &&
    typeof language_code === "string" &&
    language_code.length > 0
  )
}

/** Type-safe helper to check if a persona has an Audio Degradation preset set. */
function hasAudioDegradation(metadata: unknown): boolean {
  if (metadata == null || typeof metadata !== "object") {
    return false
  }
  if (!("audio_degradation" in metadata)) {
    return false
  }
  const audioDegradation = metadata.audio_degradation
  return audioDegradation != null && typeof audioDegradation === "object"
}

/** Type-safe helper to check if a persona has a Situate Speaker preset set. */
export function hasSituateSpeaker(metadata: unknown): boolean {
  if (metadata == null || typeof metadata !== "object") {
    return false
  }
  return (
    "situate_speaker" in metadata &&
    isSituateSpeakerPresetId(metadata.situate_speaker)
  )
}

/** Validates persona form data and applies silent mode defaults if enabled. */
export const validatePersonaForm = (
  data: unknown,
): {
  isValid: boolean
  errors: PersonaFormErrors
  validatedData?: PersonaFormData
} => {
  try {
    const validatedData = personaFormSchema.parse(data)
    return { isValid: true, errors: {}, validatedData }
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors: PersonaFormErrors = {}
      error.issues.forEach((err) => {
        if (err.path[0]) {
          errors[err.path[0] as keyof PersonaFormData] = err.message
        }
      })
      return { isValid: false, errors }
    }
    return { isValid: false, errors: { display_name: "Invalid form data" } }
  }
}
